import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { HelmetProvider, Helmet } from 'react-helmet-async';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ThemeProvider } from './context/ThemeContext';

// Common Components — loaded eagerly (always visible)
import Navbar from './components/common/Navbar';
import Footer from './components/common/Footer';
import LegalDisclaimerModal from './components/common/LegalDisclaimerModal';
import ShareButton from './components/common/ShareButton';
import ScrollToTop from './components/common/ScrollToTop';
import './App.css';

// ─── Lazy-loaded public pages ───────────────────────────────────────────────
// Each page is split into its own JS chunk, loaded only when the route is
// visited. This eliminates the "flicker" caused by parsing all page code
// on initial load and dramatically reduces Time-to-Interactive.
const Home = lazy(() => import('./components/pages/Home'));
const About = lazy(() => import('./components/pages/About'));
const AreaOfPractice = lazy(() => import('./components/pages/AreaOfPractice'));
const PracticeAreaDetail = lazy(() => import('./components/pages/PracticeAreaDetail'));
const OurPeople = lazy(() => import('./components/pages/OurPeople'));
const TeamMember = lazy(() => import('./components/pages/TeamMember'));
const LegalDisclaimer = lazy(() => import('./components/pages/LegalDisclaimer'));

// ─── Lazy-loaded insight pages ───────────────────────────────────────────────
const InsightList = lazy(() => import('./components/insights/InsightList'));
const InsightDetail = lazy(() => import('./components/insights/InsightDetail'));

// ─── Lazy-loaded admin pages ─────────────────────────────────────────────────
const AdminLogin = lazy(() => import('./components/admin/AdminLogin'));
const AdminDashboard = lazy(() => import('./components/admin/AdminDashboard'));
const AddInsight = lazy(() => import('./components/admin/AddInsight'));
const EditInsight = lazy(() => import('./components/admin/EditInsight'));
const ManageTestimonials = lazy(() => import('./components/admin/ManageTestimonials'));
const ManageFAQs = lazy(() => import('./components/admin/ManageFAQs'));
const ViewContactMessage = lazy(() => import('./components/admin/ViewContactMessage'));
const ManageContacts = lazy(() => import('./components/admin/ManageContacts'));
const NewsletterGenerator = lazy(() => import('./components/admin/NewsletterGenerator'));
const SeedData = lazy(() => import('./components/admin/SeedData'));

// ─── Minimal full-page skeleton shown while a chunk loads ────────────────────
// Matches the site's dark navy background so there's no white flash.
const PageSkeleton = () => (
  <div style={{
    minHeight: '100vh',
    background: '#0f172a',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  }}>
    <div style={{
      width: 48,
      height: 48,
      border: '3px solid rgba(197,160,89,0.2)',
      borderTopColor: '#c5a059',
      borderRadius: '50%',
      animation: 'spin 0.8s linear infinite',
    }} />
    <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
  </div>
);

// ─── Protected Route ─────────────────────────────────────────────────────────
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <PageSkeleton />;
  return isAuthenticated ? children : <Navigate to="/admin/login" />;
};


// ─── Admin noindex wrapper ────────────────────────────────────────────────────
// Prevents all /admin/* routes from being indexed by search engines.
const AdminRoute = ({ children }) => (
  <>
    <Helmet>
      <meta name="robots" content="noindex, nofollow" />
    </Helmet>
    {children}
  </>
);

function App() {
  return (
    <HelmetProvider>
      <AuthProvider>
        <ThemeProvider>
          <Router>
            <ScrollToTop />
            <div className="App">
              <LegalDisclaimerModal />
              <Navbar />

              <main className="main-content">
                {/* Suspense boundary: shows skeleton while any lazy chunk loads */}
                <Suspense fallback={<PageSkeleton />}>
                  <Routes>
                    {/* ── Public ── */}
                    <Route path="/" element={<Home />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/team/:slug" element={<TeamMember />} />
                    <Route path="/area-of-practice" element={<AreaOfPractice />} />
                    <Route path="/practice/:slug" element={<PracticeAreaDetail />} />
                    <Route path="/legal-disclaimer" element={<LegalDisclaimer />} />
                    <Route path="/our-people" element={<OurPeople />} />
                    <Route path="/insights" element={<InsightList />} />
                    <Route path="/insights/:slug" element={<InsightDetail />} />

                    {/* ── Admin ── */}
                    <Route path="/admin" element={<Navigate to="/admin/login" replace />} />
                    <Route path="/admin/login" element={<AdminRoute><AdminLogin /></AdminRoute>} />
                    <Route
                      path="/admin/dashboard"
                      element={<AdminRoute><ProtectedRoute><AdminDashboard /></ProtectedRoute></AdminRoute>}
                    />
                    <Route
                      path="/admin/insights/add"
                      element={<AdminRoute><ProtectedRoute><AddInsight /></ProtectedRoute></AdminRoute>}
                    />
                    <Route
                      path="/admin/insights/edit/:id"
                      element={<AdminRoute><ProtectedRoute><EditInsight /></ProtectedRoute></AdminRoute>}
                    />
                    <Route
                      path="/admin/testimonials"
                      element={<AdminRoute><ProtectedRoute><ManageTestimonials /></ProtectedRoute></AdminRoute>}
                    />
                    <Route
                      path="/admin/faqs"
                      element={<AdminRoute><ProtectedRoute><ManageFAQs /></ProtectedRoute></AdminRoute>}
                    />
                    <Route
                      path="/admin/contacts/:id"
                      element={<AdminRoute><ProtectedRoute><ViewContactMessage /></ProtectedRoute></AdminRoute>}
                    />
                    <Route
                      path="/admin/contacts"
                      element={<AdminRoute><ProtectedRoute><ManageContacts /></ProtectedRoute></AdminRoute>}
                    />
                    <Route
                      path="/admin/newsletter"
                      element={<AdminRoute><ProtectedRoute><NewsletterGenerator /></ProtectedRoute></AdminRoute>}
                    />
                    <Route
                      path="/admin/seed"
                      element={<AdminRoute><ProtectedRoute><SeedData /></ProtectedRoute></AdminRoute>}
                    />

                    {/* ── 404 ── */}
                    <Route path="*" element={<Navigate to="/" />} />
                  </Routes>
                </Suspense>
              </main>

              <ShareButton />
              <Footer />
            </div>
          </Router>
        </ThemeProvider>
      </AuthProvider>
    </HelmetProvider>
  );
}

export default App;